import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronLeft, Clock, CircleCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { SERVICE_GROUPS, ALL_SERVICES, VISIT_TYPE_LABELS, BUSINESS, formatTime12, formatPrice } from "../lib/data";

const STEPS = ["Service", "Date & Time", "Your Details"];

function formatDay(dateStr) {
  return new Date(`${dateStr}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function nextDays(count) {
  const days = [];
  const today = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    days.push({
      value: `${yyyy}-${mm}-${dd}`,
      weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
      day: d.getDate(),
      month: d.toLocaleDateString("en-US", { month: "short" }),
    });
  }
  return days;
}

export default function BookingWizard() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [serviceId, setServiceId] = useState(searchParams.get("service") || "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const days = useMemo(() => nextDays(28), []);
  const service = ALL_SERVICES.find((s) => s.id === serviceId);

  useEffect(() => {
    if (!date || !serviceId) return;
    setSlotsLoading(true);
    setSlots(null);
    api
      .get("/slots", { params: { date, service_id: serviceId } })
      .then((res) => setSlots(res.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [date, serviceId]);

  const refreshSlots = () => {
    if (!date) return;
    api.get("/slots", { params: { date, service_id: serviceId } }).then((res) => setSlots(res.data.slots));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        service_id: serviceId,
        date,
        time,
        name: form.name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
      });
      setConfirmation(data);
      setStep(3);
      window.scrollTo(0, 0);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("That time was just taken — please pick another slot.");
        setTime("");
        refreshSlots();
        setStep(1);
      } else {
        toast.error(apiError(err, "Booking failed. Please try again or call us."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const detailsValid =
    form.name.trim().length >= 2 &&
    /^\S+@\S+\.\S+$/.test(form.email.trim()) &&
    form.phone.trim().length >= 7;

  return (
    <div data-testid="booking-wizard" className="rounded-lg border border-linen bg-ivory p-6 sm:p-10">
      {step < 3 && (
        <div className="mb-10 flex items-center gap-3">
          {STEPS.map((label, i) => (
            <div key={label} className="flex flex-1 items-center gap-3">
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors duration-300 ${
                  i <= step ? "border-forest bg-forest text-ivory" : "border-linen text-moss"
                }`}
              >
                {i < step ? <Check size={14} /> : i + 1}
              </span>
              <span className={`hidden text-xs font-semibold uppercase tracking-[0.15em] sm:block ${i <= step ? "text-forest" : "text-moss/60"}`}>
                {label}
              </span>
              {i < STEPS.length - 1 && <span className="h-px flex-1 bg-linen" />}
            </div>
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
            <h2 className="font-serif text-2xl text-forest sm:text-3xl">What can we help you with?</h2>
            <div className="mt-8 space-y-10">
              {SERVICE_GROUPS.map((group) => (
                <div key={group.category}>
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold-dark">{group.category}</p>
                  <p className="mt-1 text-xs text-moss">{group.note}</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {group.services.map((s) => (
                      <button
                        key={s.id}
                        data-testid={`book-service-${s.id}`}
                        onClick={() => setServiceId(s.id)}
                        className={`rounded-md border p-5 text-left transition-colors duration-300 ${
                          serviceId === s.id ? "border-gold bg-cream" : "border-linen bg-ivory hover:border-gold/60"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <span className="font-serif text-lg text-forest">{s.name}</span>
                          <span className="font-serif text-lg text-gold-dark">{formatPrice(s.price)}</span>
                        </div>
                        <p className="mt-2 text-xs leading-relaxed text-moss">{s.blurb}</p>
                        <div className="mt-3 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-moss/70">
                          <span className="flex items-center gap-1"><Clock size={12} /> {s.duration} min</span>
                          <span className="rounded-full border border-linen px-2 py-0.5">{VISIT_TYPE_LABELS[s.visitType]}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 flex justify-end">
              <button
                data-testid="booking-step1-continue"
                disabled={!serviceId}
                onClick={() => setStep(1)}
                className="rounded-md bg-forest px-7 py-3 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-forest-dark disabled:cursor-not-allowed disabled:opacity-40"
              >
                Continue
              </button>
            </div>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
            <h2 className="font-serif text-2xl text-forest sm:text-3xl">Pick a date &amp; time</h2>
            <p className="mt-2 text-sm text-moss">
              {service?.name} &middot; {service?.duration} min &middot; {VISIT_TYPE_LABELS[service?.visitType]}
            </p>

            <div className="mt-7 flex gap-2 overflow-x-auto pb-3" data-lenis-prevent>
              {days.map((d) => (
                <button
                  key={d.value}
                  data-testid={`book-day-${d.value}`}
                  onClick={() => { setDate(d.value); setTime(""); }}
                  className={`flex w-[68px] shrink-0 flex-col items-center rounded-md border px-3 py-3 transition-colors duration-300 ${
                    date === d.value ? "border-gold bg-forest text-ivory" : "border-linen bg-ivory text-forest hover:border-gold/60"
                  }`}
                >
                  <span className={`text-[10px] font-semibold uppercase tracking-widest ${date === d.value ? "text-gold" : "text-moss"}`}>{d.weekday}</span>
                  <span className="mt-1 font-serif text-xl">{d.day}</span>
                  <span className={`text-[10px] ${date === d.value ? "text-ivory/70" : "text-moss/70"}`}>{d.month}</span>
                </button>
              ))}
            </div>

            <div className="mt-6 min-h-[120px]">
              {!date && <p className="text-sm text-moss">Select a day above to see available times.</p>}
              {date && slotsLoading && <p className="text-sm text-moss">Checking availability&hellip;</p>}
              {date && slots && slots.length === 0 && !slotsLoading && (
                <p data-testid="slots-empty" className="rounded-md border border-linen bg-cream p-4 text-sm text-moss">
                  No openings on {formatDay(date)}. Try another day, or call us at {BUSINESS.phone}.
                </p>
              )}
              {date && slots && slots.length > 0 && (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                  {slots.map((t) => (
                    <button
                      key={t}
                      data-testid={`book-slot-${t.replace(":", "")}`}
                      onClick={() => setTime(t)}
                      className={`rounded-md border px-3 py-2.5 text-sm font-medium transition-colors duration-300 ${
                        time === t ? "border-gold bg-gold text-forest-dark" : "border-linen bg-ivory text-forest hover:border-gold/60"
                      }`}
                    >
                      {formatTime12(t)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-10 flex items-center justify-between">
              <button data-testid="booking-step2-back" onClick={() => setStep(0)} className="flex items-center gap-2 text-sm font-semibold text-moss transition-colors hover:text-forest">
                <ChevronLeft size={16} /> Back
              </button>
              <button
                data-testid="booking-step2-continue"
                disabled={!date || !time}
                onClick={() => setStep(2)}
                className="rounded-md bg-forest px-7 py-3 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-forest-dark disabled:cursor-not-allowed disabled:opacity-40"
              >
                Continue
              </button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
            <h2 className="font-serif text-2xl text-forest sm:text-3xl">Your details</h2>

            <div className="mt-6 rounded-md border border-gold/40 bg-cream p-5 text-sm text-forest">
              <p className="font-serif text-lg">{service?.name}</p>
              <p className="mt-1 text-moss">
                {formatDay(date)} at {formatTime12(time)} &middot; {VISIT_TYPE_LABELS[service?.visitType]} &middot; {service?.price > 0 ? `$${service.price} — pay at your visit` : "Complimentary"}
              </p>
            </div>

            <div className="mt-7 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="booking-name" className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">Full Name</label>
                <input
                  id="booking-name"
                  data-testid="booking-name-input"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-gold"
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <label htmlFor="booking-phone" className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">Phone</label>
                <input
                  id="booking-phone"
                  data-testid="booking-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-gold"
                  placeholder="801-555-0100"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="booking-email" className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">Email</label>
                <input
                  id="booking-email"
                  data-testid="booking-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-gold"
                  placeholder="jane@email.com"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="booking-notes" className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">Anything Nichole should know? (optional)</label>
                <textarea
                  id="booking-notes"
                  data-testid="booking-notes-input"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-gold"
                  placeholder="Goals, questions, or health notes"
                />
              </div>
            </div>

            <div className="mt-10 flex items-center justify-between">
              <button data-testid="booking-step3-back" onClick={() => setStep(1)} className="flex items-center gap-2 text-sm font-semibold text-moss transition-colors hover:text-forest">
                <ChevronLeft size={16} /> Back
              </button>
              <button
                data-testid="booking-submit-button"
                disabled={!detailsValid || submitting}
                onClick={submit}
                className="rounded-md bg-gold px-7 py-3 text-sm font-semibold text-forest-dark transition-colors duration-300 hover:bg-gold-dark disabled:cursor-not-allowed disabled:opacity-40"
              >
                {submitting ? "Reserving..." : "Reserve My Visit"}
              </button>
            </div>
          </motion.div>
        )}

        {step === 3 && confirmation && (
          <motion.div key="s3" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="py-6 text-center">
            <CircleCheck size={56} className="mx-auto text-forest" data-testid="booking-confirmation-icon" />
            <h2 data-testid="booking-confirmation" className="mt-6 font-serif text-3xl text-forest sm:text-4xl">
              You&rsquo;re booked, {confirmation.name.split(" ")[0]}.
            </h2>
            <p className="mt-3 text-sm text-moss" data-testid="booking-confirmation-email-note">
              A confirmation email is on its way to <span className="font-medium text-forest">{confirmation.email}</span>. Nichole will be in touch shortly.
            </p>

            <div className="mx-auto mt-8 max-w-md rounded-lg border border-gold/40 bg-cream p-6 text-left">
              <p className="font-serif text-xl text-forest">{confirmation.service_name}</p>
              <dl className="mt-4 space-y-2 text-sm text-moss">
                <div className="flex justify-between"><dt>When</dt><dd className="font-medium text-forest">{formatDay(confirmation.date)} at {formatTime12(confirmation.time)}</dd></div>
                <div className="flex justify-between"><dt>Visit type</dt><dd className="font-medium text-forest">{confirmation.visit_type_label}</dd></div>
                <div className="flex justify-between"><dt>Price</dt><dd className="font-medium text-forest">{confirmation.price > 0 ? `$${confirmation.price} — pay at your visit` : "Complimentary"}</dd></div>
                <div className="flex justify-between gap-6"><dt>Reference</dt><dd className="break-all font-mono text-xs text-forest" data-testid="booking-reference">{confirmation.id}</dd></div>
              </dl>
            </div>

            {confirmation.forms_sent?.length > 0 && (
              <div data-testid="booking-forms-sent" className="mx-auto mt-6 max-w-md rounded-lg border border-linen p-6 text-left">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gold-dark">Complete before your visit</p>
                <p className="mt-2 text-sm text-moss">We&rsquo;ve emailed you these forms — or fill them out right now:</p>
                <ul className="mt-3 space-y-2">
                  {confirmation.forms_sent.map((f) => (
                    <li key={f.form_type}>
                      <a href={`/forms/${f.form_type}?booking=${confirmation.id}`} data-testid={`booking-form-link-${f.form_type}`} className="text-sm font-semibold text-forest underline underline-offset-4 hover:text-gold-dark">{f.form_name} &rarr;</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {confirmation.visit_type === "in_person" && (
              <p className="mt-6 text-sm text-moss">{BUSINESS.addressLines.join(" · ")}</p>
            )}

            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a href={`/manage/${confirmation.id}`} data-testid="booking-manage-link" className="rounded-md bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-forest-dark">
                Manage This Booking
              </a>
              <a href="/" data-testid="booking-home-link" className="rounded-md border border-forest px-6 py-3 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-forest hover:text-ivory">
                Back to Home
              </a>
              <button
                data-testid="booking-another-button"
                onClick={() => { setStep(0); setServiceId(""); setDate(""); setTime(""); setConfirmation(null); setForm({ name: "", email: "", phone: "", notes: "" }); }}
                className="rounded-md border border-forest px-6 py-3 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-forest hover:text-ivory"
              >
                Book Another Visit
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
